import React, { useEffect, useState } from 'react';
import { getTrophyStats } from '../../api';
import { useAuth } from '../../App';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS = ['#7C3AED', '#E67E22', '#27AE60', '#E74C3C', '#9B59B6', '#1ABC9C', '#F39C12', '#3498DB'];
const RISK_COLORS = { vert: '#27AE60', orange: '#F39C12', rouge: '#E74C3C' };

export default function JuryDashboard() {
  const [stats, setStats] = useState(null);
  // Cette page est ouverte au jury ET à l'administrateur. L'avancement du QIT
  // relève du suivi des candidatures, pas de l'évaluation : il est masqué au
  // jury, et reste visible à l'organisateur qui ouvre la même page.
  const { user } = useAuth();
  const showQitProgress = user?.role !== 'jury';

  useEffect(() => {
    getTrophyStats().then(setStats).catch(console.error);
  }, []);

  if (!stats) return <div className="loading">Chargement du dashboard Trophées...</div>;

  const secteurData = {
    labels: stats.by_secteur.map(s => s.secteur),
    datasets: [{ data: stats.by_secteur.map(s => parseInt(s.count)), backgroundColor: COLORS, borderWidth: 0 }],
  };

  const riskData = {
    labels: stats.by_risk.map(r => ({ vert: 'Résilient', orange: 'Vigilance', rouge: 'Critique' }[r.risk_level] || r.risk_level)),
    datasets: [{ data: stats.by_risk.map(r => parseInt(r.count)), backgroundColor: stats.by_risk.map(r => RISK_COLORS[r.risk_level] || '#999'), borderWidth: 0 }],
  };

  const scoreData = {
    labels: stats.score_distribution.map(s => s.tranche),
    datasets: [{
      label: 'Nombre de startups',
      data: stats.score_distribution.map(s => parseInt(s.count)),
      backgroundColor: stats.score_distribution.map(s =>
        s.tranche.includes('Excellent') ? '#27AE60' : s.tranche.includes('Bon') ? '#2ECC71' :
        s.tranche.includes('Moyen') ? '#F39C12' : s.tranche.includes('Faible') ? '#E67E22' : '#E74C3C'
      ),
      borderRadius: 6,
    }],
  };

  const qit = stats.qit_stats;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ marginBottom: 4 }}>Dashboard Trophées de l'Innovation</h1>
          <p style={{ color: 'var(--text-light)', fontSize: 14 }}>
            Statistiques basées sur les <strong>{stats.total} candidatures soumises</strong> à la campagne en cours
          </p>
          <div style={{ display: 'inline-block', padding: '6px 14px', background: '#FFF8E1', borderRadius: 8, fontSize: 12, color: '#F57F17', marginTop: 8 }}>
            Périmètre : toutes les candidatures soumises ({stats.total}), y compris celles qui ne font pas partie
            de la sélection soumise au jury. Les dossiers non soumis et le reste de l'annuaire sont exclus.
            {stats.scored !== undefined && stats.scored < stats.total && (
              <> Les indicateurs de score portent sur les <strong>{stats.scored}</strong> candidatures déjà notées.</>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="stats-grid" style={{ marginBottom: 32 }}>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--primary)' }}>{stats.total}</div>
            <div className="stat-label">Candidatures soumises</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E67E22' }}>{stats.averages?.avg_sectorielle || '-'}</div>
            <div className="stat-label">Note ISI moyenne</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#2E75B6' }}>{stats.averages?.avg_pivot || '-'}</div>
            <div className="stat-label">Note Pivot moyenne</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#27AE60' }}>{Math.max(0, Math.min(100, qit?.avg_progress || 0))}%</div>
            <div className="stat-label">QIT complétion moyenne</div>
          </div>
        </div>

        {/* QIT Progress — organisateur uniquement */}
        {showQitProgress && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Avancement du questionnaire QIT</h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', flex: '1 1 90px' }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#27AE60' }}>{qit?.qit_complete || 0}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Complets (&ge; 80%)</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#F39C12' }}>{qit?.qit_partial || 0}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Partiels (&lt; 80%)</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#E74C3C' }}>{qit?.qit_empty || 0}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Non commencés</div>
            </div>
          </div>
        </div>
        )}

        <div className="resp-2col" style={{ marginBottom: 24 }}>
          {/* Score distribution */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Distribution des scores ISI (candidatures notées)</h3>
            <div style={{ height: 250 }}>
              <Bar data={scoreData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
            </div>
          </div>

          {/* Risk distribution */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Indicateurs de risque (candidatures notées)</h3>
            <div style={{ height: 250, display: 'flex', justifyContent: 'center' }}>
              <Doughnut data={riskData} options={{ responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } } }} />
            </div>
          </div>

          {/* By sector */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Répartition sectorielle (candidatures soumises)</h3>
            <div style={{ height: 250, display: 'flex', justifyContent: 'center' }}>
              <Doughnut data={secteurData} options={{ responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } } }} />
            </div>
          </div>

          {/* By stade */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Par stade de développement (candidatures soumises)</h3>
            {stats.by_stade.map(s => {
              const pct = (parseInt(s.count) / stats.total * 100);
              return (
                <div key={s.stade_dev} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>{s.stade_dev}</span><span>{s.count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{ height: 8, background: '#F0F0F0', borderRadius: 4 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cluster averages */}
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Moyennes par cluster (candidatures notées)</h3>
          <div className="resp-4col keep-grid">
            {[
              { key: 'avg_bp', label: 'Business Plan' }, { key: 'avg_ratio', label: 'Ratio Efficacité' },
              { key: 'avg_solidite', label: 'Solidité Fi.' }, { key: 'avg_maturite', label: 'Maturité Fi.' },
              { key: 'avg_marche', label: 'Marché' }, { key: 'avg_innovation', label: 'Innovation' },
              { key: 'avg_equipe', label: 'Équipe' }, { key: 'avg_org_comm', label: 'Org. Comm.' },
              { key: 'avg_notoriete', label: 'Notoriété' }, { key: 'avg_rse', label: 'RSE' },
              { key: 'avg_rgpd', label: 'RGPD' }, { key: 'avg_conformite', label: 'Conformité' },
            ].map(c => {
              const val = parseFloat(stats.averages?.[c.key]) || 0;
              const color = val > 7 ? '#27AE60' : val >= 4 ? '#F39C12' : '#E74C3C';
              return (
                <div key={c.key} style={{ textAlign: 'center', padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color }}>{val.toFixed(1)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>{c.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 10 */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Top 10 des candidatures soumises</h3>
          <div style={{ display: 'grid', gap: 6 }}>
            {stats.top10.map((s, i) => {
              const pct = (parseFloat(s.note_sectorielle) / 10) * 100;
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '';
              return (
                <div key={i} className="rank-row">
                  <span style={{ width: 28, textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{medal || `#${i + 1}`}</span>
                  <span className="rank-name" style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
                  <span className="badge badge-secteur" style={{ fontSize: 9, flexShrink: 0 }}>{s.categorie}</span>
                  <div className="rank-bar" style={{ height: 20, background: '#F0F0F0', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 10,
                      background: pct > 70 ? 'linear-gradient(90deg, #27AE60, #2ECC71)' : pct > 40 ? 'linear-gradient(90deg, #F39C12, #F1C40F)' : 'linear-gradient(90deg, #E74C3C, #E67E22)' }} />
                  </div>
                  <span style={{ width: 50, textAlign: 'right', fontWeight: 700, fontSize: 14,
                    color: parseFloat(s.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                    {parseFloat(s.note_sectorielle).toFixed(1)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
